import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InfiniteData, QueryClient, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { newsErrorReason } from '../utils/news.utils'
import { useCurrentUser } from '../../../hooks/useUser'
import { useCallFirebaseFunction } from '../../../hooks/useFunctions'
import { QueryKeys } from '../../../constants/queryKeys.constants'
import { NewsComment, NewsMutation, NewsMutationInput, NewsMutationResult, NewsPage, NewsPost } from '../types/news.types'
import { readNews, clearNewsImages } from '../services/news.service'
import { clearNewsDraftsAtom } from '../state/newsDrafts'

export const newsQueryOptions = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchInterval: false as const,
  retry: false,
}

export const newsRootKey = (uid: string | undefined) => [...QueryKeys.NEWS, uid] as const
export const newsFeedKey = (uid: string | undefined) => [...newsRootKey(uid), QueryKeys.NEWS_FEED] as const
export const newsPostKey = (uid: string | undefined, postId: string) => [...newsRootKey(uid), QueryKeys.NEWS_POST, postId] as const
export const newsCommentsKey = (uid: string | undefined, postId: string) => [...newsRootKey(uid), QueryKeys.NEWS_COMMENTS, postId] as const

type NewsItem = NewsPost | NewsComment
type NewsInfinite<T extends NewsItem> = InfiniteData<NewsPage<T>, string | null>

const newer = <T extends NewsItem>(current: T | undefined, incoming: T) => (!current || incoming.version >= current.version ? incoming : current)

function normalizedPages<T extends NewsItem>(data: NewsInfinite<T>): NewsInfinite<T> {
  const preferred = new Map<string, T>()
  data.pages.forEach((page) => page.items.forEach((item) => preferred.set(item.id, newer(preferred.get(item.id), item))))
  const seen = new Set<string>()
  let changed = false
  const pages = data.pages.map((page) => ({
    ...page,
    items: page.items.flatMap((item) => {
      if (seen.has(item.id)) {
        changed = true
        return []
      }
      seen.add(item.id)
      const selected = preferred.get(item.id)!
      if (selected !== item) changed = true
      return [selected]
    }),
  }))
  return changed ? { ...data, pages } : data
}

function updateInfiniteItem<T extends NewsItem>(
  client: QueryClient,
  key: readonly unknown[],
  id: string,
  item?: T,
  prepend = false,
  commentCount?: number
) {
  client.setQueryData<NewsInfinite<T>>(key, (current) => {
    if (!current) return current
    const existing = current.pages.flatMap((page) => page.items).find((value) => value.id === id)
    const selected = item ? newer(existing, item) : undefined
    const pages = current.pages.map((page) => ({
      ...page,
      ...(commentCount !== undefined ? { commentCount } : {}),
      items: page.items.flatMap((value) => (value.id === id ? (selected ? [selected] : []) : [value])),
    }))
    if (prepend && selected) {
      const first = pages[0]
      if (first) pages[0] = { ...first, items: [selected, ...first.items.filter((value) => value.id !== id)] }
      else pages.push({ items: [selected], nextCursor: null, ...(commentCount !== undefined ? { commentCount } : {}) })
    }
    return normalizedPages({ ...current, pages })
  })
}

function updatePostCount(client: QueryClient, uid: string | undefined, postId: string, count: number) {
  client.setQueryData<NewsPost>(newsPostKey(uid, postId), (post) => (post ? { ...post, commentCount: count } : post))
  client.setQueryData<NewsInfinite<NewsPost>>(newsFeedKey(uid), (current) => {
    if (!current) return current
    return {
      ...current,
      pages: current.pages.map((page) => ({
        ...page,
        items: page.items.map((post) => (post.id === postId ? { ...post, commentCount: count } : post)),
      })),
    }
  })
}

export function useNewsCache() {
  const client = useQueryClient()
  const { user } = useCurrentUser()
  const uid = user?.uid
  const setPost = useCallback(
    (post: NewsPost, prepend = false) => {
      client.setQueryData<NewsPost>(newsPostKey(uid, post.id), (current) => newer(current, post))
      updateInfiniteItem(client, newsFeedKey(uid), post.id, post, prepend)
    },
    [client, uid]
  )
  const deletePost = useCallback(
    (postId: string) => {
      updateInfiniteItem<NewsPost>(client, newsFeedKey(uid), postId)
      client.removeQueries({ queryKey: newsPostKey(uid, postId), exact: true })
      client.removeQueries({ queryKey: newsCommentsKey(uid, postId), exact: true })
    },
    [client, uid]
  )
  const setComment = useCallback(
    (postId: string, comment: NewsComment | undefined, commentId: string, count: number, prepend = false) => {
      updateInfiniteItem(client, newsCommentsKey(uid, postId), commentId, comment, prepend, count)
      updatePostCount(client, uid, postId, count)
    },
    [client, uid]
  )
  const setCommentCount = useCallback((postId: string, count: number) => updatePostCount(client, uid, postId, count), [client, uid])
  return useMemo(() => ({ setPost, deletePost, setComment, setCommentCount }), [setPost, deletePost, setComment, setCommentCount])
}

export function useNewsPost(postId: string, enabled = true) {
  const { user } = useCurrentUser()
  const client = useQueryClient()
  const cache = useNewsCache()
  return useQuery({
    queryKey: newsPostKey(user?.uid, postId),
    queryFn: async () => {
      const post = await readNews<NewsPost>('getMemberPost', { postId })
      cache.setPost(post)
      return client.getQueryData<NewsPost>(newsPostKey(user?.uid, post.id)) || post
    },
    ...newsQueryOptions,
    enabled: !!user && enabled,
  })
}

export function useNewsPages<T extends NewsPost | NewsComment>(postId?: string) {
  const client = useQueryClient()
  const { user } = useCurrentUser()
  const key = postId ? newsCommentsKey(user?.uid, postId) : newsFeedKey(user?.uid)
  const name = postId ? 'listMemberPostComments' : 'listMemberPosts'
  const moreLock = useRef(false)
  const query = useInfiniteQuery({
    queryKey: key,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const page = await readNews<NewsPage<T>>(name, {
        ...(postId ? { postId } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      })
      if (!postId) {
        for (const post of page.items as NewsPost[]) {
          client.setQueryData<NewsPost>(newsPostKey(user?.uid, post.id), (current) => newer(current, post))
        }
      }
      return page
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    select: normalizedPages,
    ...newsQueryOptions,
    enabled: !!user,
  })
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) || [], [query.data])
  const nextCursor = query.data?.pages.at(-1)?.nextCursor || null
  const loadMore = useCallback(async () => {
    if (moreLock.current || !query.hasNextPage) return
    moreLock.current = true
    try {
      await query.fetchNextPage()
    } finally {
      moreLock.current = false
    }
  }, [query.fetchNextPage, query.hasNextPage])
  const refresh = useCallback(() => query.refetch(), [query.refetch])
  return {
    ...query,
    items,
    nextCursor,
    commentCount: query.data?.pages.at(-1)?.commentCount,
    moreLoading: query.isFetchingNextPage,
    moreError: query.isFetchNextPageError ? query.error : null,
    loadMore,
    refresh,
  }
}

export function useNewsMutation(name: NewsMutation) {
  const mutation = useCallFirebaseFunction<NewsMutationInput, NewsMutationResult>(name, undefined, { retry: false })
  const locked = useRef(false)
  const blocked = useRef(false)
  const [requiresReload, setRequiresReload] = useState(false)
  const acknowledgeReload = () => {
    blocked.current = false
    setRequiresReload(false)
  }
  const run = async (payload: NewsMutationInput) => {
    if (blocked.current) throw Object.assign(new Error('MUTATION_UNCERTAIN'), { details: { reason: 'MUTATION_UNCERTAIN' } })
    if (locked.current) throw Object.assign(new Error('OPERATION_IN_PROGRESS'), { details: { reason: 'OPERATION_IN_PROGRESS' } })
    locked.current = true
    try {
      return await mutation.mutateAsync(payload)
    } catch (error) {
      const code = (error as { code?: string })?.code
      if (
        !newsErrorReason(error) &&
        !['functions/invalid-argument', 'functions/permission-denied', 'functions/unauthenticated'].includes(code || '')
      ) {
        blocked.current = true
        setRequiresReload(true)
        throw Object.assign(new Error('MUTATION_UNCERTAIN'), { details: { reason: 'MUTATION_UNCERTAIN' }, cause: error })
      }
      throw error
    } finally {
      locked.current = false
    }
  }
  return { ...mutation, run, requiresReload, acknowledgeReload }
}

export function NewsSessionBoundary() {
  const { user } = useCurrentUser()
  const client = useQueryClient()
  const clearDrafts = useSetAtom(clearNewsDraftsAtom)
  useEffect(
    () => () => {
      if (!user?.uid) return
      void client.cancelQueries({ queryKey: newsRootKey(user.uid) })
      client.removeQueries({ queryKey: newsRootKey(user.uid) })
      clearDrafts(user.uid)
      clearNewsImages()
    },
    [user?.uid, client, clearDrafts]
  )
  return null
}
